import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import _ from 'lodash';

import arrowIosForwardFill from '@iconify-icons/eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify-icons/eva/arrow-ios-downward-fill';
import { alpha, makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge
} from '@material-ui/core';
import { setIsClickNavbar } from 'src/redux/slices/consignmentStockReturnable';
import { useDispatch } from 'react-redux';

// ----------------------------------------------------------------------
const storageContainerName = process.env.REACT_APP_STORAGE_CONTAINER

const useStyles = makeStyles((theme) => ({
  root: {},
  listItem: {
    ...theme.typography.body2,
    height: 48,
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary
  },
  listItemCloseNav: {
    ...theme.typography.body2,
    height: 60,
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    flexDirection: "column",
    justifyContent: "center"
  },
  listItemTextCloseNav: {
    marginLeft: '-6px',
    marginRight: 'auto',
    fontSize: 12,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '160%',
    whiteSpace: 'no-wrap'
  },
  subIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:before': {
      width: 4,
      height: 4,
      content: "''",
      display: 'block',
      borderRadius: '50%',
      backgroundColor: theme.palette.text.disabled,
      transition: theme.transitions.create('transform')
    }
  },
  isActiveListItem: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      position: 'absolute',
      backgroundColor: theme.palette.primary.main
    }
  },
  isActiveListItemSub: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    '& $subIcon:before': {
      transform: 'scale(2)',
      backgroundColor: theme.palette.primary.main
    }
  },
  customBadge: {
    backgroundColor: 'orange',
    color: 'white', 
    paddingBottom: '2px'
  }
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  level: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  info: PropTypes.element,
  icon: PropTypes.element,
  badgeModule: PropTypes.string,
  badgeContent: PropTypes.number,
  open: PropTypes.bool,
  drawerOpen: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  disabledCollapse: PropTypes.bool
};

function NavItem({
  level,
  title,
  captionTitle,
  href,
  info,
  icon,
  badgeModule,
  badgeContent,
  open = false,
  children,
  className,
  drawerOpen,
  disabledCollapse,
  ...other
}) {
  const classes = useStyles();
  const [show, setShow] = useState(open);
  const isSubItem = level > 0;

  const dispatch = useDispatch()


  const handleShow = () => {
    setShow((show) => !show);
  };

  if (children) {
    return (
      <>
        <ListItem
          button
          disableGutters
          onClick={handleShow}
          className={clsx(
            classes.listItem,
            {
              [classes.isActiveListItem]: open
            },
            className
          )}
          {...other}
        >
          <ListItemIcon>{icon && icon}</ListItemIcon>
          <ListItemText disableTypography primary={title} />
          {info && info}
          {!disabledCollapse && (
            <Box
              component={Icon}
              icon={show ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{ width: 16, height: 16, ml: 1 }}
            />
          )}
        </ListItem>

        {disabledCollapse ? (
          <>{children}</>
        ) : (
          <Collapse in={show}>{children}</Collapse>
        )}
      </>
    );
  }

  return (
    <ListItem
      button

      exact={open}
      disableGutters
      activeClassName={
        isSubItem ? classes.isActiveListItemSub : classes.isActiveListItem
      }
      {...(title === "user guide" ? {} : { to: href, component: RouterLink })}
      className={drawerOpen ? clsx(classes.listItem, className) : clsx(classes.listItemCloseNav, className)}
      onClick={() => {
        if (title === "Stock Returnable" || captionTitle === "Stock Returnable") {
          dispatch(setIsClickNavbar(true))
        }

        if (title == "user guide") {
          window.open(`https://betobee.blob.core.windows.net/${storageContainerName}/Betobee_RetailerGuide_v2.0.pdf`, "_blank")
        }

        // CLEAR ORDER PAGINATION STATES
        if (title === 'orders') {
          localStorage.removeItem("orderAllPage")
          localStorage.removeItem("orderAllPageSize")
          localStorage.removeItem("orderUnpaidPage")
          localStorage.removeItem("orderUnpaidPageSize")
          localStorage.removeItem("orderPartialPage")
          localStorage.removeItem("orderPartialPageSize")
          localStorage.removeItem("orderPaidPage")
          localStorage.removeItem("orderPaidPageSize")
          localStorage.removeItem("orderProcessingPage")
          localStorage.removeItem("orderProcessingPageSize")
          localStorage.removeItem("orderToShipPage")
          localStorage.removeItem("orderToShipPageSize")
          localStorage.removeItem("orderToReceivePage")
          localStorage.removeItem("orderToReceivePageSize")
          localStorage.removeItem("orderCompletePage")
          localStorage.removeItem("orderCompletePageSize")
        }
      }}
      isActive={(match, location) => {
        if (!match) {
          return false;
        }
        const { url } = match;
        const { pathname } = location;
        const isMatch = url === pathname;

        if (!isSubItem) {
          return url.length && pathname.includes(url);
        }

        return isMatch;
      }}
      {...other}
    >
      <ListItemIcon className={classes.listItemIcon}>
        {isSubItem ? <span className={classes.subIcon} /> : icon}
      </ListItemIcon>
      <ListItemText disableTypography primary={title} className={drawerOpen ? '' : classes.listItemTextCloseNav}/>

      {info && info} {badgeContent > 0 && (<Badge classes={{ badge: classes.customBadge }} badgeContent={badgeContent}/>)}
    </ListItem>    
  );
}

export default NavItem;